import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bug, Loader2, Mail, Save, Trash2, Reply, CheckCircle2, RotateCcw, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const fmt = (s) => (s ? new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "–");

function Shot({ id }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/problem-reports/${id}/screenshot`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [id]);
  if (!url) return <div className="h-32 w-48 rounded-lg bg-slate-100 animate-pulse" />;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="Screenshot" className="max-h-64 rounded-lg border border-slate-200" data-testid={`problem-report-shot-${id}`} /></a>;
}

function EmailSetting() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/problem-reports/settings/email").then((r) => setEmail(r.data.email || "")).catch(() => {}); }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/problem-reports/settings/email", { email }); toast.success("Empfänger-Adresse gespeichert"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 mb-6" data-testid="problem-email-setting">
      <div className="font-semibold flex items-center gap-2 mb-1"><Mail className="h-4 w-4 text-primary" />Meldungen senden an</div>
      <p className="text-sm text-slate-500 mb-3">An diese E-Mail-Adresse wird jede neue Problem-Meldung geschickt.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="problem-email-setting-input" />
        <Button type="submit" disabled={busy} data-testid="problem-email-setting-save"><Save className="h-4 w-4 mr-2" />Speichern</Button>
      </div>
    </form>
  );
}

function ReplyBox({ r, onDone, onCancel }) {
  const [text, setText] = useState("");
  const [markDone, setMarkDone] = useState(true);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/problem-reports/${r.id}/reply`, { message: text, mark_done: markDone });
      toast.success(`Antwort an ${r.email} gesendet`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={`problem-reply-box-${r.id}`}>
      <div className="text-xs text-slate-500">Antwort an <b>{r.email}</b></div>
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ihre Antwort …" className="bg-white" data-testid={`problem-reply-input-${r.id}`} />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <Checkbox checked={markDone} onCheckedChange={(v) => setMarkDone(!!v)} data-testid={`problem-reply-done-${r.id}`} />Zugleich als erledigt markieren
        </label>
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`problem-reply-cancel-${r.id}`}>Abbrechen</Button>
          <Button size="sm" disabled={busy || !text.trim()} onClick={send} data-testid={`problem-reply-send-${r.id}`}>
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}Senden
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReportRow({ r, focus, reload }) {
  const [replying, setReplying] = useState(false);
  const toggleDone = async () => {
    try { await api.put(`/problem-reports/${r.id}/done`, { done: !r.done }); toast.success(r.done ? "Meldung wieder geöffnet" : "Als erledigt markiert"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!window.confirm("Meldung löschen?")) return;
    try { await api.delete(`/problem-reports/${r.id}`); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <li className={`py-4 ${focus === r.id ? "bg-amber-50 -mx-4 px-4 sm:-mx-6 sm:px-6" : ""}`} data-testid={`problem-report-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold flex items-center gap-2 flex-wrap">{r.name} <span className="font-normal text-slate-500">· {r.email}</span>
            {r.done && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5" data-testid={`problem-report-done-badge-${r.id}`}><CheckCircle2 className="h-3 w-3" />Erledigt</span>}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">{fmt(r.created_at)} · Konto {r.account_email} · {r.role} · Version {r.app_version || "–"} · Seite {r.page || "–"}{r.email_sent ? "" : " · E-Mail nicht versendet"}</div>
        </div>
        <button onClick={remove} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600" data-testid={`problem-report-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <p className="text-sm text-slate-700 whitespace-pre-wrap mt-2">{r.message}</p>
      {r.has_screenshot && <div className="mt-3"><Shot id={r.id} /></div>}
      {(r.replies || []).map((a, i) => (
        <div key={i} className="mt-3 border-l-2 border-primary pl-3 text-sm" data-testid={`problem-report-reply-${r.id}-${i}`}>
          <div className="text-xs text-slate-400">Antwort von {a.by} · {fmt(a.at)}</div>
          <p className="text-slate-700 whitespace-pre-wrap">{a.message}</p>
        </div>
      ))}
      {replying ? <ReplyBox r={r} onCancel={() => setReplying(false)} onDone={() => { setReplying(false); reload(); }} /> : (
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={() => setReplying(true)} data-testid={`problem-report-reply-btn-${r.id}`}><Reply className="h-4 w-4 mr-1" />Per E-Mail antworten</Button>
          <Button size="sm" variant="ghost" onClick={toggleDone} data-testid={`problem-report-toggle-done-${r.id}`}>
            {r.done ? <><RotateCcw className="h-4 w-4 mr-1" />Wieder öffnen</> : <><CheckCircle2 className="h-4 w-4 mr-1" />Als erledigt markieren</>}
          </Button>
        </div>
      )}
    </li>
  );
}

export default function ProblemReports() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("open");
  const [params] = useSearchParams();
  const focus = params.get("id");
  const load = useCallback(() => { api.get("/problem-reports").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (focus && items) document.querySelector(`[data-testid="problem-report-${focus}"]`)?.scrollIntoView({ block: "center" });
  }, [focus, items]);
  const shown = items && items.filter((r) => (tab === "done" ? r.done : !r.done));
  const openCount = items ? items.filter((r) => !r.done).length : 0;
  return (
    <div className="max-w-3xl" data-testid="problem-reports-page">
      <h1 className="text-4xl sm:text-5xl font-head font-extrabold tracking-tight flex items-center gap-3"><Bug className="h-9 w-9 text-primary" />Problem-Meldungen</h1>
      <p className="text-slate-500 mt-2 mb-6">Gemeldete Fehler aus „Probleme melden“ im Profilmenü.</p>
      <EmailSetting />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="open" data-testid="problem-tab-open">Offen ({openCount})</TabsTrigger>
          <TabsTrigger value="done" data-testid="problem-tab-done">Erledigt</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="mt-4 bg-white rounded-2xl border border-slate-200 px-4 sm:px-6">
        {items === null ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : shown.length === 0 ? <p className="py-10 text-center text-slate-500" data-testid="problem-reports-empty">Keine Meldungen.</p>
          : <ul className="divide-y divide-slate-100">{shown.map((r) => <ReportRow key={r.id} r={r} focus={focus} reload={load} />)}</ul>}
      </div>
    </div>
  );
}
